import { createFileRoute, Link } from "@tanstack/react-router";
import { TackLogo } from "@/components/TackLogo";
import { openCookiePreferences } from "@/components/CookieBanner";
import { SiteFooter } from "@/components/SiteFooter";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Tack. Privacy Policy" },
      { name: "description", content: "How Tack by Lineri AB collects, uses, and protects merchant and customer personal data." },
      { property: "og:title", content: "Tack. Privacy Policy" },
      { property: "og:description", content: "How Tack by Lineri AB handles your personal data." },
    ],
    links: [{ rel: "canonical", href: "https://tack.company/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <Link to="/" aria-label="Tack home" className="inline-block hover:opacity-80 transition-opacity">
            <TackLogo size="md" />
          </Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-display font-bold mb-8">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: {new Date().toLocaleDateString("en-SE")}</p>

        <div className="prose prose-sm max-w-none space-y-6 text-foreground/90 leading-relaxed">
          <section>
            <h2 className="text-xl font-display font-semibold mb-3">1. Data Controller</h2>
            <p><strong>Lineri AB</strong> (trading as "Tack.") is the data controller responsible for personal data processed in connection with the Tack service. We are registered in Sweden. For any questions about this policy, email <a href="mailto:hello@tack.company" className="text-primary underline">hello@tack.company</a>.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">2. Who this policy covers</h2>
            <p>Tack handles two distinct groups of people. We treat their data differently:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Merchants</strong> — account holders who sign up for Tack to run feedback and rewards at their business.</li>
              <li><strong>Customers</strong> — people who scan a Tack QR code at a merchant's location and submit feedback.</li>
            </ul>
            <p className="mt-2">For customer data, the merchant is the joint controller of the mobile phone numbers and consent records they collect. We process that data on their behalf under a Data Processing Agreement (see <Link to="/dpa" className="text-primary underline">/dpa</Link>).</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">3. Data we collect from merchants</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Account data</strong> — name, email address, login credentials</li>
              <li><strong>Business data</strong> — business name, branding, feedback settings, offer/reward configuration</li>
              <li><strong>Billing-related data</strong> — Lineri AB (Tack) is the seller for all plans and the starter kit. Payments are processed by Stripe, which also handles VAT calculation, invoicing and receipts on our behalf; we receive subscription status, plan, and invoice metadata only. We never see or store your full card details.</li>
              <li><strong>Usage data</strong> — pages visited, features used, session duration</li>
              <li><strong>Device & technical data</strong> — IP address, browser type, device type</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">4. Data we collect from customers</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Mobile phone number</strong> — required to deliver the reward code by SMS to the customer who requested it</li>
              <li><strong>Star rating</strong> (1–5)</li>
              <li><strong>Optional comment</strong></li>
              <li><strong>Scan timestamp</strong></li>
              <li><strong>Consent records</strong> — whether the customer opted in to receive the reward SMS and (optionally) marketing messages from the merchant</li>
              <li><strong>Truncated IP and device type</strong> — for rate-limiting and fraud prevention only</li>
            </ul>
            <p className="mt-3 font-medium">What we never collect from customers:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Names</li>
              <li>Phone numbers</li>
              <li>Cross-site tracking or browsing history</li>
              <li>Purchase history or payment details</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">5. How we use merchant data</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Account creation & authentication</strong> — to set up and secure your account</li>
              <li><strong>Service delivery</strong> — feedback collection, analytics, rewards, multi-location, staff access</li>
              <li><strong>Billing</strong> — coordinated with Stripe for subscription management, invoicing, and payment processing</li>
              <li><strong>Customer support</strong> — to respond to your inquiries</li>
              <li><strong>Product improvement</strong> — to understand usage patterns and improve the service</li>
              <li><strong>Security & fraud prevention</strong> — to protect the service and our users</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">6. How we use customer data</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Send the one-time discount code</strong> — contract performance; necessary to deliver the reward the customer asked for.</li>
              <li><strong>Send a "We Miss You" nudge</strong> from the merchant if enabled — legitimate interest of the merchant, balanced with one-click unsubscribe in every email.</li>
              <li><strong>Anonymised aggregate analytics for the merchant</strong> — rating distributions, scan counts, no individual identification.</li>
            </ul>
            <p className="mt-3 font-medium">What we never do with customer data:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Sell it to anyone</li>
              <li>Share it with other merchants</li>
              <li>Use it for ad targeting</li>
              <li>Enrich it with third-party data sources</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">7. Legal basis</h2>
            <p>We process personal data on the following bases:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Contract performance</strong> — providing the service and delivering rewards customers request</li>
              <li><strong>Legitimate interests</strong> — analytics, security, service improvement, retention nudges from the merchant</li>
              <li><strong>Consent</strong> — where applicable (e.g. optional analytics cookies)</li>
              <li><strong>Legal obligation</strong> — where required by law (tax records, etc.)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">8. Sub-processors</h2>
            <p>We share data with the following Sub-processors, each under a written agreement and only for the purposes listed:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Supabase</strong> (EU region) — database hosting, authentication, file storage</li>
              <li><strong>Cloudflare Workers</strong> — edge runtime that serves the application</li>
              <li><strong>Stripe</strong> — payment processing, subscription management, VAT calculation, invoicing and receipts on our behalf</li>
              <li><strong>Resend</strong> — transactional email delivery to merchants (welcome, trial reminders, account notifications)</li>
              <li><strong>GatewayAPI</strong> (EU-hosted) — SMS delivery of reward codes and, where the customer opted in, marketing messages from the merchant</li>
              <li><strong>Lovable</strong> — application hosting platform</li>
              <li><strong>Plausible Analytics</strong> (EU-hosted) — privacy-first, cookieless website analytics. No personal data, no cross-site tracking.</li>
              <li><strong>Professional advisers</strong> — legal and accounting, where required</li>
              <li><strong>Authorities</strong> — where required by law</li>
            </ul>
            <p className="mt-2">We commit to giving at least 30 days' notice of any material change to this list. The current list above is canonical; for the latest version email <a href="mailto:hello@tack.company" className="text-primary underline">hello@tack.company</a>.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">9. International transfers</h2>
            <p>Customer data primarily stays in the EU via our Supabase EU region. Where any data is transferred outside the EEA, we ensure appropriate safeguards are in place — typically Standard Contractual Clauses or an adequacy decision under GDPR Chapter V.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">10. Data retention</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Merchant account data</strong> — kept for the lifetime of the account, deleted or anonymised within 30 days of account closure. Some billing records are retained by Stripe for up to 7 years to satisfy Swedish tax law.</li>
              <li><strong>Customer feedback rows</strong> (rating + comment) — 24 months from submission, then anonymised: the email is removed and the rating is kept solely for the merchant's aggregated metrics.</li>
              <li><strong>Customer email on the unsubscribe list</strong> — kept indefinitely as a suppression list, so we can honour the unsubscribe.</li>
              <li><strong>Logs and analytics events</strong> — 12 months.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">11. Customer rights & how to exercise them</h2>
            <p>If you submitted feedback at a Tack-using business and want to exercise your rights, you can either:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Email us directly at <a href="mailto:hello@tack.company" className="text-primary underline">hello@tack.company</a>, or</li>
              <li>Ask the merchant who collected your data — either route works.</li>
            </ul>
            <p className="mt-2">We respond within 30 days. You also have the right to lodge a complaint with the Swedish Authority for Privacy Protection (<strong>IMY</strong>, <a href="https://www.imy.se" target="_blank" rel="noopener noreferrer" className="text-primary underline">imy.se</a>) or your local supervisory authority.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">12. Your rights as a merchant</h2>
            <p>Under applicable data protection law (including GDPR), you have the right to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Access your personal data</li>
              <li>Rectify inaccurate data</li>
              <li>Request erasure of your data</li>
              <li>Restrict processing</li>
              <li>Data portability</li>
              <li>Object to processing</li>
              <li>Withdraw consent at any time</li>
              <li>Lodge a complaint with your local supervisory authority</li>
            </ul>
            <p className="mt-2">We will respond to requests within one month.</p>
            <p className="mt-2">You can exercise the access (export) and erasure (delete) rights yourself from <strong>Settings → Privacy &amp; data</strong>. For the others, email <a href="mailto:hello@tack.company" className="text-primary underline">hello@tack.company</a>.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">13. Security</h2>
            <p>We implement appropriate technical and organisational measures to protect data, including encryption in transit and at rest, Row-Level Security isolating each merchant's data, role-based access controls, automated backups with point-in-time recovery, and incident response procedures including 72-hour breach notification.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">14. Cookies</h2>
            <p>Tack uses two categories of cookies:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Essential</strong> — authentication, security, and saved settings. Always on; the service won't work without them.</li>
              <li><strong>Analytics</strong> — anonymised usage data so we can improve the product. Optional.</li>
            </ul>
            <p className="mt-2">EU/EEA visitors see a banner on first visit to choose. You can change your choice anytime via{" "}
              <button type="button" onClick={openCookiePreferences} className="text-primary underline">Cookie preferences</button>{" "}in the page footer.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">15. Changes to This Policy</h2>
            <p>We may update this privacy policy from time to time. We will notify you of material changes via email or in-app notification.</p>
          </section>

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">16. Contact</h2>
            <p>For privacy-related inquiries, contact us at <a href="mailto:hello@tack.company" className="text-primary underline">hello@tack.company</a>.</p>
          </section>
        </div>
      </main>

      <SiteFooter variant="legal" />
    </div>
  );
}
